import React, { useState, useEffect } from 'react';
import { X, Eye, TrendingUp, Calendar, Globe, MapPin, UserPlus, Loader2, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const AnalyticsModal = ({ isOpen, onClose, cardId, cardName, cardUsername, currentUserId, showAssignButton = false, adminEmail = null }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignEmail, setAssignEmail] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  useEffect(() => {
    if (isOpen && cardId) {
      fetchAnalytics();
    }
  }, [isOpen, cardId]);

  const { toast } = useToast();

  // Safe date formatting helper
  const formatDate = (dateValue, options = { month: 'short', day: 'numeric' }) => {
    try {
      if (!dateValue) return 'Unknown';
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.warn('Error formatting date:', dateValue, error);
      return 'Invalid Date';
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const { analyticsAPI } = await import('../services/api');
      
      // Fetch card views data
      const viewsData = await analyticsAPI.getCardViews(cardId);
      
      // Fetch link clicks data
      const clicksData = await analyticsAPI.getCardLinkClicks(cardId);
      
      // Fetch contact saves data
      const savesData = await analyticsAPI.getCardSaves(cardId).catch(() => ({ total_saves: 0, recent_saves: [] }));
      
      // Process views data
      const allViews = viewsData.views || [];
      const totalViews = viewsData.totalViews || 0;
      const uniqueViews = viewsData.uniqueViewers || 0;
      
      // Process saves data
      const totalSaves = savesData.total_saves || 0;
      const recentSaves = savesData.recent_saves || [];
      
      // Process link clicks data
      const allClicks = clicksData.clicks || [];
      const totalClicks = clicksData.totalClicks || 0;
      const linkStats = clicksData.linkStats || [];
      
      // Process daily views from the views array - ensure we have 7 days of data
      const processedDailyViews = [];
      const today = new Date();
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateString = date.toISOString().split('T')[0];
        
        // Count views for this specific day with date validation
        const dayViews = allViews.filter(view => {
          try {
            // Validate and parse the date
            if (!view.viewedAt) return false;
            
            // Handle various date formats
            const viewDate = new Date(view.viewedAt);
            
            // Check if date is valid
            if (isNaN(viewDate.getTime())) {
              console.warn('Invalid date in view:', view.viewedAt);
              return false;
            }
            
            const viewDateString = viewDate.toISOString().split('T')[0];
            return viewDateString === dateString;
          } catch (error) {
            console.warn('Error parsing view date:', view.viewedAt, error);
            return false;
          }
        }).length;
        
        processedDailyViews.push({
          date: dateString,
          views: dayViews
        });
      }
      
      // Extract locations from view data (using new location fields)
      const locationCounts = {};
      allViews.forEach(view => {
        // Try to get location from direct fields first, then viewerInfo
        let location = 'Unknown';
        if (view.city && view.city !== 'Unknown') {
          location = view.country && view.country !== 'Unknown' 
            ? `${view.city}, ${view.country}` 
            : view.city;
        } else if (view.country && view.country !== 'Unknown') {
          location = view.country;
        } else if (view.viewerInfo?.location?.city) {
          const loc = view.viewerInfo.location;
          location = loc.country && loc.country !== 'Unknown'
            ? `${loc.city}, ${loc.country}`
            : loc.city;
        } else if (view.viewerInfo?.location) {
          location = typeof view.viewerInfo.location === 'string' 
            ? view.viewerInfo.location 
            : 'Unknown';
        }
        locationCounts[location] = (locationCounts[location] || 0) + 1;
      });
      
      // Sort locations by count and get top 5
      const topLocations = Object.entries(locationCounts)
        .map(([location, count]) => ({ location, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      // Calculate engagement rate
      const engagementRate = totalViews > 0 
        ? ((uniqueViews / totalViews) * 100).toFixed(1)
        : 0;
      
      // Calculate average daily views
      const totalDailyViews = processedDailyViews.reduce((sum, day) => sum + day.views, 0);
      const avgDailyViews = (totalDailyViews / 7).toFixed(1);
      
      // Get peak day
      const peakDay = processedDailyViews.reduce((max, day) => 
        day.views > max.views ? day : max, 
        { views: 0, date: '' }
      );
      
      setAnalytics({
        totalViews,
        uniqueViews,
        dailyViews: processedDailyViews,
        locations: topLocations,
        engagementRate,
        avgDailyViews,
        peakDay,
        totalClicks,
        linkStats: linkStats.sort((a, b) => b.count - a.count),
        recentClicks: allClicks.slice(0, 5),
        totalSaves,
        recentSaves
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = () => {
    setAssignEmail('');
    setAssignDialog(true);
  };

  const confirmAssign = async () => {
    if (!assignEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }
    
    if (!adminEmail) {
      toast({
        title: "Error",
        description: "Admin email not provided",
        variant: "destructive",
      });
      return;
    }
    
    setAssignLoading(true);
    try {
      const { cardAPI } = await import('../services/api');
      
      // Call the assign API endpoint for regular cards
      await cardAPI.assignToUser(cardId, assignEmail.trim(), adminEmail);
      
      toast({
        title: "Success",
        description: `Card assigned to ${assignEmail}`,
      });
      
      setAssignDialog(false);
      setAssignEmail('');
      
      // Close the analytics modal and notify parent
      onClose();
    } catch (error) {
      console.error('Error assigning card:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to assign card",
        variant: "destructive",
      });
    } finally {
      setAssignLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-yellow-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">Card Analytics</h2>
              <p className="text-amber-100 text-sm">{cardName}</p>
            </div>
            <div className="flex items-center gap-2">
              {showAssignButton && (
                <Button
                  onClick={handleAssign}
                  className="bg-white text-amber-600 hover:bg-amber-50"
                  size="sm"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading analytics...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-red-600 font-medium">{error}</p>
              <Button onClick={fetchAnalytics} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-3 border border-amber-200">
                  <div className="flex flex-col items-start">
                    <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center mb-2">
                      <Eye className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-xs text-gray-600">Total Views</p>
                    <p className="text-xl font-bold text-gray-900">{analytics.totalViews}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 border border-green-200">
                  <div className="flex flex-col items-start">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mb-2">
                      <Download className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-xs text-gray-600">Contact Saves</p>
                    <p className="text-xl font-bold text-gray-900">{analytics.totalSaves || 0}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-3 border border-teal-200">
                  <div className="flex flex-col items-start">
                    <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center mb-2">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-xs text-gray-600">Unique Views</p>
                    <p className="text-xl font-bold text-gray-900">{analytics.uniqueViews}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 border border-blue-200">
                  <div className="flex flex-col items-start">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mb-2">
                      <Calendar className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-xs text-gray-600">Avg/Day</p>
                    <p className="text-xl font-bold text-gray-900">{analytics.avgDailyViews}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-3 border border-orange-200">
                  <div className="flex flex-col items-start">
                    <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center mb-2">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-xs text-gray-600">Engagement</p>
                    <p className="text-xl font-bold text-gray-900">{analytics.engagementRate}%</p>
                  </div>
                </div>
              </div>

              {/* Quick Stats Summary */}
              {analytics.totalViews > 0 && (
                <div className="bg-gradient-to-r from-amber-600 to-yellow-500 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90 mb-1">Peak Performance Day</p>
                      <p className="text-lg font-bold">
                        {analytics.peakDay.views > 0 
                          ? formatDate(analytics.peakDay.date)
                          : 'No data yet'
                        }
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm opacity-90 mb-1">Views</p>
                      <p className="text-2xl font-bold">{analytics.peakDay.views}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Daily Views Chart */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-gray-900">Last 7 Days</h3>
                </div>
                <div className="space-y-2">
                  {analytics.dailyViews.map((day, index) => {
                    const maxViews = Math.max(...analytics.dailyViews.map(d => d.views || 0), 1);
                    const percentage = ((day.views || 0) / maxViews) * 100;
                    
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-20">
                          {formatDate(day.date)}
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-amber-600 to-yellow-500 h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                            style={{ width: `${percentage}%` }}
                          >
                            {day.views > 0 && (
                              <span className="text-xs text-white font-medium">{day.views}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Link Clicks Analytics */}
              <div className="bg-gradient-to-br from-teal-50 to-amber-50 rounded-xl p-4 border border-teal-200">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-teal-600" />
                  <h3 className="font-semibold text-gray-900">Link Clicks</h3>
                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full font-medium">
                    {analytics.totalClicks || 0} total
                  </span>
                </div>
                
                {analytics.linkStats && analytics.linkStats.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.linkStats.map((stat, index) => {
                      const maxClicks = Math.max(...analytics.linkStats.map(s => s.count), 1);
                      const percentage = (stat.count / maxClicks) * 100;
                      const iconColors = {
                        email: 'bg-blue-100 text-blue-600',
                        phone: 'bg-green-100 text-green-600',
                        website: 'bg-amber-100 text-amber-600',
                        location: 'bg-red-100 text-red-600',
                        linkedin: 'bg-blue-100 text-blue-700',
                        twitter: 'bg-sky-100 text-sky-600',
                        github: 'bg-gray-100 text-gray-800',
                        instagram: 'bg-pink-100 text-pink-600',
                        youtube: 'bg-red-100 text-red-600',
                        facebook: 'bg-blue-100 text-blue-700'
                      };
                      
                      return (
                        <div key={index} className="bg-white rounded-lg p-3 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`w-8 h-8 rounded-lg ${iconColors[stat.linkType] || 'bg-gray-100 text-gray-600'} flex items-center justify-center text-sm font-semibold`}>
                                {stat.linkType?.charAt(0).toUpperCase() || '?'}
                              </span>
                              <span className="font-medium text-gray-900 capitalize">{stat.linkType || 'Unknown'}</span>
                            </div>
                            <span className="text-lg font-bold text-teal-600">{stat.count}</span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-teal-500 to-amber-500 h-full rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-6 text-center">
                    <div className="flex justify-center mb-3">
                      <TrendingUp className="w-12 h-12 text-gray-300" />
                    </div>
                    <p className="text-gray-500 font-medium">No link clicks yet</p>
                    <p className="text-sm text-gray-400 mt-1">Link clicks will appear here once visitors interact with your contact links</p>
                  </div>
                )}
              </div>

              {/* Top Locations */}
              {analytics.locations.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-gray-900">Top Locations</h3>
                  </div>
                  <div className="space-y-3">
                    {analytics.locations.map((location, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-amber-600">#{index + 1}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{location.location || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">{location.count} {location.count === 1 ? 'view' : 'views'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-amber-600 to-yellow-500 h-full rounded-full"
                              style={{ width: `${(location.count / analytics.totalViews) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-amber-600 w-12 text-right">
                            {((location.count / analytics.totalViews) * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Data Message */}
              {analytics.totalViews === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Eye className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Views Yet</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Your card hasn't been viewed yet. Share it to start tracking analytics!
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-md mx-auto">
                    <p className="text-sm text-blue-900 font-medium mb-2">💡 Tips to get more views:</p>
                    <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                      <li>Share your QR code on social media</li>
                      <li>Add your card link to email signatures</li>
                      <li>Include it in your LinkedIn profile</li>
                      <li>Print QR codes on business materials</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 rounded-b-2xl">
          <Button 
            onClick={onClose}
            className="w-full bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600"
          >
            Close
          </Button>
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Assign Card to User
            </DialogTitle>
            <DialogDescription>
              Enter the email address of the user you want to assign this card to.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{cardName}</p>
              {cardUsername && (
                <p className="text-xs text-gray-500 mt-1">@{cardUsername}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="assignEmail" className="text-sm font-medium">
                User Email Address *
              </Label>
              <Input
                id="assignEmail"
                type="email"
                placeholder="user@example.com"
                value={assignEmail}
                onChange={(e) => setAssignEmail(e.target.value)}
                className="mt-1"
                disabled={assignLoading}
              />
              <p className="text-xs text-gray-500 mt-1">
                The card will be transferred to this user's account
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setAssignDialog(false)}
              disabled={assignLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmAssign}
              disabled={assignLoading || !assignEmail.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {assignLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Card
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalyticsModal;
